import { BehaviorSubject, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { User } from '../models/user.model';
import { environment } from 'src/environments/environment';
import {jwtDecode} from 'jwt-decode';
import { CommonApiResponse } from '../models/common-api-response.model';

@Injectable({ providedIn: 'root' })
export class AuthService {

  private currentUserSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  public currentUser: Observable<User | null> = this.currentUserSubject.asObservable();
  private isLoggingOut = false;

  private mqttConnectionSubject = new Subject<any>();
  public mqttConnection$ = this.mqttConnectionSubject.asObservable();
  private refreshTokenTimerSubscription: Subscription | null = null;
  private refreshTokenRequest$: Observable<string | null> | null = null;


  constructor(private _http: HttpClient, private _router: Router) {
    const user = this.getCurrentUser();
    this.currentUserSubject.next(user);
    this.currentUser = this.currentUserSubject.asObservable();
    this.setupTokenRefresh();

  }

  setupTokenRefresh() {
    this.scheduleTokenRefresh(this.currentUserValue?.jwt);
  }

  private scheduleTokenRefresh(token?: string | null): void {
    this.refreshTokenTimerSubscription?.unsubscribe();
    this.refreshTokenTimerSubscription = null;

    if (this.isLoggingOut || !token) {
      return;
    }

    const delayMs = this.getTokenRefreshDelayMs(token);
    if (delayMs === null) {
      return;
    }

    const refreshDelayMs = Math.max(delayMs, 0);

    this.refreshTokenTimerSubscription = timer(refreshDelayMs)
      .pipe(
        switchMap(() => {
          if (this.isLoggingOut || !this.currentUserValue?.jwt) {
            return of(null);
          }

          return this.refreshToken();
        }),
        catchError(() => of(null))
      )
      .subscribe();
  }

  private getTokenRefreshDelayMs(token: string, bufferSeconds: number = 30): number | null {
    if (!token) {
      return null;
    }

    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      return null;
    }

    try {
      const payload = this.decodeJwtPayload(token);
      if (!payload?.exp) {
        return null;
      }

      const expirationTime = payload.exp * 1000;
      const bufferMs = bufferSeconds * 1000;
      return expirationTime - Date.now() - bufferMs;
    } catch (error) {
      console.error('[AUTH] Error calculating token refresh delay:', error);
      return null;
    }
  }

  // getter: currentUserValue
  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }


  login(email: string, password: string, totpCode: string) {
    const formData: any = {
      email: email,
      password: password,
      totp_code: totpCode?.length === 0 ? null : totpCode,
    };
  
    return this._http.post<any>(`${environment.base_url}/login`, formData).pipe(
      switchMap(response => {
        if (!response) {
          return of(response);
        }

        const decodedToken = jwtDecode(response.jwt);
        const user = new User();
        user.fromJson({ ...response, ...decodedToken });
        user.email = email;
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.currentUserSubject.next(user);

        return this._http.get<CommonApiResponse>(`${environment.base_url}/users/userinfo`).pipe(
          map((userInfoResponse: CommonApiResponse) => {
            this.setUserProfileOnCurrentUser(user, userInfoResponse?.data);
            this.prepareMqttClientId(user);
            this.completeLogin(user);
            return response;
          }),
          catchError(() => {
            this.prepareMqttClientId(user);
            this.completeLogin(user);
            return of(response);
          })
        );
      })
    );
  }

  logout(): void {
    const endpoint = `${environment.base_url}/logout`;
    this.isLoggingOut = true;

    const user = this.getCurrentUser();
    if (!user?.jwt || this.isTokenExpired(user.jwt)) {
      this.clearAuthData();
      return;
    }

    this._http.post<CommonApiResponse>(endpoint, {}).subscribe({
      next: () => this.clearAuthData(),
      error: () => this.clearAuthData()
    });
  }
  public clearExpiredSession(navigateToLogin: boolean = true): void {
    this.clearAuthData(navigateToLogin);
  }

  private clearAuthData(navigateToLogin: boolean = true) {
    this.isLoggingOut = false;
    this.scheduleTokenRefresh(null);
    this.triggerMqttConnection(false, true);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('mqttClientName');
    sessionStorage.removeItem('mqttUser');
    this.currentUserSubject.next(null);

    if (navigateToLogin && !this._router.url.includes('/auth/reset-password')) {
      this._router.navigate(['/auth/login']);
    }
  }


  getCurrentUser(): User | null {
    const userJson = localStorage.getItem('currentUser');
    if (!userJson) {
      return null;
    }

    let userData: any;
    try {
      userData = JSON.parse(userJson);
    } catch (error) {
      localStorage.removeItem('currentUser');
      return null;
    }

    if (!userData?.jwt && !userData?.jwt_refresh) {
      return null;
    }

    const user = new User();
    return user.fromJson(userData);
  }


  checkTokenValidity(token:string){
    const endpoint = `${environment.base_url}/users/check_reset_token/${token}`
    return this._http.get<CommonApiResponse>(endpoint)
  }

  forgetPassword(requestBody: any){
    const endpoint = `${environment.base_url}/users/forget_password`;
    return this._http.post<CommonApiResponse>(endpoint, requestBody)
  }

  resetPassword(requestBody: any){
    const endpoint = `${environment.base_url}/users/reset_password`;
    return this._http.post<CommonApiResponse>(endpoint, requestBody)
  }

  isTokenExpired(token: string, bufferSeconds: number = 30): boolean {
    if (!token) {
      return true;
    }

    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      return true;
    }

    try {
      const payload = this.decodeJwtPayload(token);
      if (!payload || !payload.exp) {
        return true;
      }

      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();
      
      // Add buffer to consider token expired before actual expiration
      const bufferMs = bufferSeconds * 1000;
      
      return (expirationTime - bufferMs) < currentTime;
    } catch (error) {
      console.error('[AUTH] Error parsing token:', error);
      return true;
    }
  }

  private decodeJwtPayload(token: string): any {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');

    return JSON.parse(atob(paddedBase64));
  }

  refreshToken() {
    const user = this.getCurrentUser();

    if (this.isLoggingOut || !user?.jwt_refresh) {
      return of(null);
    }

    if (this.refreshTokenRequest$) {
      return this.refreshTokenRequest$;
    }

    console.log('[AUTH] Refreshing JWT token...');

    this.refreshTokenRequest$ = this._http
      .post<any>(`${environment.base_url}/login/refresh`, null)
      .pipe(
        map(response => {
          if (!response?.jwt) {
            return null;
          }

          const decodedToken = jwtDecode(response.jwt);
          user.jwt = response.jwt;
          Object.assign(user, decodedToken);
          localStorage.setItem('currentUser', JSON.stringify(user));
          this.currentUserSubject.next(user);
          console.log('[AUTH] JWT token refreshed successfully, exp:', new Date(decodedToken.exp * 1000));
          console.log('[AUTH] Triggering MQTT reconnect after token refresh');
          this.triggerMqttConnection(true, false, response.jwt);
          this.scheduleTokenRefresh(response.jwt);
          return response.jwt;
        }),
        catchError(error => {
          console.error('[AUTH] Token refresh failed:', error);
          throw error;
        }),
        finalize(() => {
          this.refreshTokenRequest$ = null;
        }),
        shareReplay(1)
      );

    return this.refreshTokenRequest$;
  }

  triggerMqttConnection(refresh: boolean, disconnect: boolean, jwt?: string) {
    this.mqttConnectionSubject.next({ refresh, disconnect, jwt });
  }

  private completeLogin(user: User): void {
    this._router.navigate(['/app']);
    this.triggerMqttConnection(true, false, user.jwt);
    this.scheduleTokenRefresh(user.jwt);
  }

  private setUserProfileOnCurrentUser(user: User, profile: any): void {
    if (!profile) {
      return;
    }

    user.first_name = profile.first_name;
    user.last_name = profile.last_name;
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  private prepareMqttClientId(user?: any): void {
    if (sessionStorage.getItem('mqttUser')) {
      return;
    }

    const fullName = `${user?.first_name || ''}${user?.last_name || ''}`;
    const clientName = this.sanitizeMqttClientName(fullName || localStorage.getItem('mqttClientName') || user?.email);
    const randomNumber = Math.floor(100000 + Math.random() * 900000);

    localStorage.setItem('mqttClientName', clientName);
    sessionStorage.setItem('mqttUser', `WEBclient_${clientName}_${randomNumber}`);
  }

  private sanitizeMqttClientName(username: string): string {
    return String(username || '')
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, '_') || 'anonymous';
  }
}
